import React, {useCallback, useEffect, useState} from 'react';
import {Navigate, useSearchParams} from "react-router-dom";
import {observer} from "mobx-react-lite";
import {toast} from "react-toastify";
import BaseLayout from "../../layouts/BaseLayout";
import CompanyObserver from "../../mobx/company/CompanyObserver";
import FetchService from "../../services/api/FetchService";
import {IChecklistManagementResource} from "../../resources/IChecklistManagement.resource";
import {ErrorToastSettings} from "../../services/toast/toastSettings";
import Loader from "../../components/Loader";
import CustomPagination from "../AutomaticAssessmentPage/components/Pagination";
import ChecklistManagementTable from "./components/ChecklistManagementTable";
import UploadChecklistModal from "./components/UploadChecklistModal";
import useHasChecklistManagementAccess from "../../hooks/useHasChecklistManagementAccess";
import ROUTES from "../../routes";
import styles from './styles.module.scss';

const DEFAULT_PER_PAGE = 20;

const ChecklistManagementPage: React.FC = () => {
    const {isLoading: isLoadingAccess, hasAccess} = useHasChecklistManagementAccess();
    const [searchParams, setSearchParams] = useSearchParams();
    const [checklists, setChecklists] = useState<IChecklistManagementResource[]>([]);
    const [total, setTotal] = useState<number>(0);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isUploadOpen, setIsUploadOpen] = useState<boolean>(false);

    const page = Number(searchParams.get('page') ?? '1');
    const perPage = Number(searchParams.get('perPage') ?? String(DEFAULT_PER_PAGE));

    const companyId = CompanyObserver.companyId ?? CompanyObserver.getCompanyId();

    const setPage = (nextPage: number) => {
        setSearchParams((prev) => {
            prev.set('page', String(nextPage));
            return prev;
        });
    };

    const loadChecklists = useCallback(async () => {
        if (!companyId) return;

        try {
            setIsLoading(true);
            const {data} = await FetchService.getChecklistManagementList(companyId, {
                pageSize: perPage,
                pageToken: String(page - 1),
            });
            setChecklists(data.checklists);
            setTotal(data.total);
        } catch (error) {
            toast.error('Не вдалося завантажити чеклісти', ErrorToastSettings);
        } finally {
            setIsLoading(false);
        }
    }, [companyId, page, perPage]);

    useEffect(() => {
        loadChecklists();
    }, [loadChecklists]);

    const handleUploaded = () => {
        setIsUploadOpen(false);
        loadChecklists();
    };

    const handleDeleted = () => {
        loadChecklists();
    };

    if (!isLoadingAccess && !hasAccess) {
        return <Navigate to={ROUTES.HOME} replace/>;
    }

    return (
        <>
            {isLoading ? <Loader/> : null}
            <BaseLayout title={'Чеклісти'}>
                <div className={styles.checklistManagement__header}>
                    <div className={styles.checklistManagement__total}>
                        Загальна кількість: {total}
                    </div>
                    <button
                        type={'button'}
                        className={styles.checklistManagement__uploadBtn}
                        onClick={() => setIsUploadOpen(true)}
                    >
                        Завантажити чеклист
                    </button>
                </div>
                {isUploadOpen ? (
                    <UploadChecklistModal
                        handleClose={() => setIsUploadOpen(false)}
                        handleUploaded={handleUploaded}
                    />
                ) : null}
                <ChecklistManagementTable data={checklists} onDeleted={handleDeleted}/>
                <div className={styles.checklistManagement__footer}>
                    <CustomPagination
                        total={total}
                        perPage={perPage}
                        page={page}
                        setPage={setPage}
                    />
                </div>
            </BaseLayout>
        </>
    );
};

export default observer(ChecklistManagementPage);
